import { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { API } from "@/config/site";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) return setError("This password-reset link is missing its token.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div className="neon-page flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl border border-primary/20 bg-card p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
        <h1 className="mt-5 font-display text-3xl font-semibold">Password updated</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Your password has been changed successfully. You can now sign in with your new password.</p>
        <Button onClick={() => navigate("/")} className="mt-7 w-full">Continue to ZEYINX</Button>
      </div>
    </div>
  );

  return (
    <div className="neon-page flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-card p-8">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to ZEYINX</Link>
        <div className="mt-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><KeyRound className="h-6 w-6" /></div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight">Reset your password</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Enter a new password for your ZEYINX account. This link is valid for one hour.</p>
        {!token && <div className="mt-6 rounded-xl border border-red-500/25 bg-red-500/[0.07] px-4 py-3 text-sm text-red-300">This reset link is invalid or missing. Please request a new one.</div>}
        {error && <div className="mt-6 rounded-xl border border-red-500/25 bg-red-500/[0.07] px-4 py-3 text-sm text-red-300">{error}</div>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><label htmlFor="reset-password" className="mb-2 block text-xs font-medium text-muted-foreground">New password</label><Input id="reset-password" type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" disabled={!token || loading} /></div>
          <div><label htmlFor="reset-password-confirm" className="mb-2 block text-xs font-medium text-muted-foreground">Confirm new password</label><Input id="reset-password-confirm" type="password" autoComplete="new-password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Enter it again" disabled={!token || loading} /></div>
          <Button type="submit" disabled={!token || loading} className="w-full">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password</Button>
        </form>
      </div>
    </div>
  );
}
